import {Fragment, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useFeature} from "flagged";
import MenuList from "@mui/material/MenuList";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import {matchPath, useLocation} from "react-router-dom";
import {useAbility} from "@casl/react";

import {
  BANNERS_PATH,
  BANNER_ROOT_PATH,
  COMPANY_INFO_ROOT_PATH,
  CUSTOMERS_PATH,
  LOCATIONS_ROOT_PATH,
  ORDERS_PATH,
  PAYMENT_INFO_ROOT_PATH,
  WEBSHOP_PRODUCTS_PATH,
  WEBSHOP_PRODUCTS_ROOT_PATH,
  DISCOUNT_ROOT_PATH,
  COLLECTION_ROOT_PATH,
  DISCOUNTS_PATH,
  COLLECTIONS_PATH,
  SHIPPING_RATES_ROOT_PATH,
  SHIPPING_RATES_PREVIEW_PATH,
  STOCK_MOVEMENTS_ROOT_PATH,
  CONFIGURATION_ROOT_PATH,
  USERS_PATH,
  BALANCE_PATH,
  WAREHOUSES_ROOT_PATH,
  LISTINGS_PATH,
  COMMERCE_RATES_ROOT_PATH,
  FULFILLMENT_RULES_ROOT_PATH,
  BRANDS_PATH,
  FINANCIAL_STATEMENTS_PATH,
  ORDER_TABS_PATH,
  BUSINESS_TOOLS_PATH,
  PRODUCTS_PATH,
  REPORTS_PATH,
  PRODUCT_PRICES_BULK_PREVIEW_PATH,
  CAMPAIGNS_LIST_PATH,
  CAMPAIGNS_TABS_PATH,
  ORDER_SETTLEMENTS_PATH,
  NOTIFICATIONS_PATH,
  ACTIVITIES_PATH,
  CONFIGURATION_COUNTRY_PREVIEW_PATH,
} from "~/modules/routing";
import {useAppSelector, strongSelectMaker, selectUser} from "~/modules/redux";
import {
  BmFileIcon,
  BmProductsIcon,
  BmSettingsIcon,
  BmShippingIcon,
  BmShoppingIcon,
  BmUsersIcon,
  BmEnergyIcon,
  BmWarehouseIcon,
  BmBookOpenIcon,
  BmGlobeIcon,
  BmDollarIcon,
  BmCampaignsIcon,
  BmActivityIcon,
} from "~/components/icons/simpleIcons";
import {designColors} from "~/ui";
import {VerticalAlignBetween} from "~/layouts/flexLayout";
import {MakerFeatures} from "~/features";
import {WATS_NEW_URL} from "~/constants/constants";
import {WarehouseKind, useLazyGetAllWarehousesQuery, useLazyGetOrderTabsMetadataQuery} from "~/api/generated";
import {Can, AbilityContext} from "~/modules/roles";
import {useCompanyInfoContext} from "~/modules/company";
import {useSelectedStore, useStorePath} from "~/hooks/private/useSelectedStore";
import {useAllStores} from "~/hooks/private/useAllStores";

import {SidebarItem} from "../SidebarItem/SidebarItem";
import {SidebarSubItem} from "../SidebarSubItem/SidebarSubItem";
import {SidebarSubList} from "../SidebarSubList/SidebarSubList";
import {SidebarLink} from "../SidebarLink/SidebarLink";
import {SidebarSection} from "../SidebarSection/SidebarSection";
import {SidebarItemContent} from "../SidebarItemContent/SidebarItemContent";
import {SidebarUser} from "../SidebarUser/SidebarUser";
import {SidebarItemBadge} from "../SidebarItemBadge/SidebarItemBadge";

export const SidebarList: React.FC = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const abilities = useAbility(AbilityContext);

  const currentStoreCountry = localStorage.getItem("commerceCountry");

  const maker = useAppSelector(strongSelectMaker);
  const {unreadCounts} = useAppSelector(selectUser);

  const [storeId, setStoreId] = useSelectedStore();
  const storePath = useStorePath();
  const companyInfo = useCompanyInfoContext();
  const storefront = useFeature(MakerFeatures.Storefront);

  const {stores} = useAllStores();
  const [getAllWarehouses, {data: allWarehousesData}] = useLazyGetAllWarehousesQuery();
  const [getOrderTabsMetadata, {data: orderTabsMetadata}] = useLazyGetOrderTabsMetadataQuery();

  const isNotMaker = useMemo(() => maker === null, [maker]);
  const isAnyWarehouse = useMemo(() => !!allWarehousesData?.warehouses.length, [allWarehousesData?.warehouses]);
  const isOnlyExternalWarehouse = useMemo(
    () => allWarehousesData?.warehouses.every((warehouse) => warehouse.kind === WarehouseKind.External),
    [allWarehousesData?.warehouses],
  );
  const isStockMovement = useMemo(
    () => !maker.trade && !isOnlyExternalWarehouse,
    [maker.trade, isOnlyExternalWarehouse],
  );
  const isClickAndCollect = useMemo(() => {
    const externalWarehouse = allWarehousesData?.warehouses.find(({kind}) => kind === WarehouseKind.External);

    if (!externalWarehouse) return false;
    if (externalWarehouse.country !== companyInfo?.country) return false;
    return true;
  }, [allWarehousesData?.warehouses, companyInfo?.country]);

  useEffect(() => {
    abilities.can("read", "fulfillment") && getAllWarehouses({makerId: maker.id});
    abilities.can("read", "order") && getOrderTabsMetadata({makerId: maker.id});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, maker.id, storeId, getAllWarehouses, getOrderTabsMetadata]);

  return (
    <Box component={VerticalAlignBetween} minHeight="auto" height="100%">
      <MenuList sx={{p: 0}}>
        <Stack spacing={4} useFlexGap>
          <SidebarSection>
            <Can I="read" a="order">
              <SidebarItem
                disabled={isNotMaker}
                selected={[ORDERS_PATH, ORDER_TABS_PATH].some((pattern) =>
                  matchPath({path: pattern, end: true}, location.pathname),
                )}
              >
                <SidebarLink to={ORDERS_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.orders")}
                    icon={<BmShippingIcon id="orders-icon" color={designColors.white} size="xs" />}
                    badge={
                      !!orderTabsMetadata?.orderTabsMetadata.newTabCount && (
                        <SidebarItemBadge>
                          {t("sidebar.amount_new", {amount: orderTabsMetadata.orderTabsMetadata.newTabCount})}
                        </SidebarItemBadge>
                      )
                    }
                  />
                </SidebarLink>
              </SidebarItem>
            </Can>

            <Can I="read" a="products">
              <SidebarSection>
                <SidebarSubList
                  id="products"
                  title={t("sidebar.group.products")}
                  icon={<BmProductsIcon id="products-icon" color={designColors.white} size="xs" />}
                  path={[PRODUCTS_PATH, PRODUCT_PRICES_BULK_PREVIEW_PATH]}
                  tooltipTitle={t("sidebar.tooltip")}
                  disabled={isNotMaker}
                >
                  <SidebarSubItem
                    title={t("sidebar.products")}
                    to={PRODUCTS_PATH}
                    path={PRODUCTS_PATH}
                    disabled={isNotMaker}
                  />

                  <SidebarSubItem
                    title={t("sidebar.product_prices")}
                    to={PRODUCT_PRICES_BULK_PREVIEW_PATH}
                    path={PRODUCT_PRICES_BULK_PREVIEW_PATH}
                    disabled={isNotMaker}
                  />
                </SidebarSubList>
              </SidebarSection>
            </Can>

            <Can I="read" a="customers">
              <SidebarItem
                disabled={isNotMaker}
                selected={!!matchPath({path: CUSTOMERS_PATH, end: false}, location.pathname)}
              >
                <SidebarLink to={CUSTOMERS_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.customers")}
                    icon={<BmUsersIcon id="customers-icon" color={designColors.white} size="xs" />}
                  />
                </SidebarLink>
              </SidebarItem>
            </Can>

            <Can I="read" a="activity">
              <SidebarItem
                disabled={isNotMaker}
                selected={!!matchPath({path: ACTIVITIES_PATH, end: false}, location.pathname)}
              >
                <SidebarLink to={ACTIVITIES_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.activities")}
                    icon={<BmActivityIcon id="activities-icon" color={designColors.white} size="xs" />}
                    badge={
                      !!unreadCounts?.activities && (
                        <SidebarItemBadge>
                          {t("sidebar.amount_new", {amount: unreadCounts.activities})}
                        </SidebarItemBadge>
                      )
                    }
                  />
                </SidebarLink>
              </SidebarItem>
            </Can>

            <Can I="read" a="campaigns">
              <SidebarItem
                disabled={isNotMaker}
                selected={[CAMPAIGNS_LIST_PATH, CAMPAIGNS_TABS_PATH].some((pattern) =>
                  matchPath({path: pattern, end: true}, location.pathname),
                )}
              >
                <SidebarLink to={CAMPAIGNS_LIST_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.campaigns")}
                    icon={<BmCampaignsIcon id="campaign-icon" color={designColors.white} size="xs" />}
                  />
                </SidebarLink>
              </SidebarItem>
            </Can>

            <Can I="read" a="report">
              <SidebarItem
                disabled={isNotMaker}
                selected={!!matchPath({path: REPORTS_PATH, end: false}, location.pathname)}
              >
                <SidebarLink to={REPORTS_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.reports")}
                    icon={<BmFileIcon id="file-icon" color={designColors.white} size="xs" />}
                  />
                </SidebarLink>
              </SidebarItem>
            </Can>
          </SidebarSection>

          <Can I="read" a="commerce">
            <SidebarSection>
              {stores.length > 0 ? (
                stores.map((store) => {
                  const matchFor = (pattern: string) => pattern.replace(":storeId", store.id);
                  const linkFor = (rootPattern: string, countryPattern: string) =>
                    currentStoreCountry
                      ? storePath(countryPattern, {storeId: store.id, country: currentStoreCountry})
                      : storePath(rootPattern, {storeId: store.id});

                  return (
                    <SidebarSubList
                      key={store.id}
                      id={`market-${store.id}`}
                      title={
                        stores.length > 1
                          ? t("labels.store_shop", {storeName: store.name})
                          : t("sidebar.group.commerce")
                      }
                      icon={<BmShoppingIcon id={`market-icon-${store.id}`} color={designColors.white} size="xs" />}
                      path={[
                        linkFor(WEBSHOP_PRODUCTS_ROOT_PATH, WEBSHOP_PRODUCTS_PATH),
                        storePath(WEBSHOP_PRODUCTS_ROOT_PATH, {storeId: store.id}),
                        storePath(COLLECTION_ROOT_PATH, {storeId: store.id}),
                        storePath(DISCOUNT_ROOT_PATH, {storeId: store.id}),
                        storePath(SHIPPING_RATES_ROOT_PATH, {storeId: store.id}),
                        storePath(BANNER_ROOT_PATH, {storeId: store.id}),
                        storePath(CONFIGURATION_ROOT_PATH, {storeId: store.id}),
                      ]}
                      tooltipTitle={t("sidebar.tooltip")}
                      disabled={isNotMaker}
                      onClick={() => setStoreId(store.id)}
                    >
                      <SidebarSubItem
                        title={t("sidebar.webshop_products")}
                        to={linkFor(WEBSHOP_PRODUCTS_ROOT_PATH, WEBSHOP_PRODUCTS_PATH)}
                        path={matchFor(WEBSHOP_PRODUCTS_PATH)}
                        disabled={isNotMaker}
                      />
                      {storefront && (
                        <>
                          <SidebarSubItem
                            title={t("sidebar.collections")}
                            to={linkFor(COLLECTION_ROOT_PATH, COLLECTIONS_PATH)}
                            path={matchFor(COLLECTIONS_PATH)}
                            disabled={isNotMaker}
                          />
                          <SidebarSubItem
                            title={t("sidebar.discounts")}
                            to={linkFor(DISCOUNT_ROOT_PATH, DISCOUNTS_PATH)}
                            path={matchFor(DISCOUNTS_PATH)}
                            disabled={isNotMaker}
                          />
                          <SidebarSubItem
                            title={t("sidebar.banners")}
                            to={linkFor(BANNER_ROOT_PATH, BANNERS_PATH)}
                            path={matchFor(BANNERS_PATH)}
                            disabled={isNotMaker}
                          />
                          <SidebarSubItem
                            title={t("sidebar.shipping_rates")}
                            to={linkFor(SHIPPING_RATES_ROOT_PATH, SHIPPING_RATES_PREVIEW_PATH)}
                            path={matchFor(SHIPPING_RATES_PREVIEW_PATH)}
                            disabled={isNotMaker}
                          />
                          <SidebarSubItem
                            title={t("sidebar.webshop_settings")}
                            to={linkFor(CONFIGURATION_ROOT_PATH, CONFIGURATION_COUNTRY_PREVIEW_PATH)}
                            path={CONFIGURATION_ROOT_PATH}
                            disabled={isNotMaker}
                          />
                        </>
                      )}
                    </SidebarSubList>
                  );
                })
              ) : (
                <SidebarItem
                  disabled={isNotMaker}
                  selected={!!matchPath({path: COMMERCE_RATES_ROOT_PATH, end: false}, location.pathname)}
                >
                  <SidebarLink
                    to={storePath(COMMERCE_RATES_ROOT_PATH)}
                    tooltipTitle={t("sidebar.tooltip")}
                    disabled={isNotMaker}
                  >
                    <SidebarItemContent
                      text={t("sidebar.group.commerce")}
                      icon={<BmShoppingIcon id="market-icon" color={designColors.white} size="xs" />}
                    />
                  </SidebarLink>
                </SidebarItem>
              )}
            </SidebarSection>
          </Can>

          <Can I="read" a="listings">
            <SidebarSection>
              <SidebarItem
                disabled={isNotMaker}
                selected={!!matchPath({path: LISTINGS_PATH, end: false}, location.pathname)}
              >
                <SidebarLink to={LISTINGS_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.listings")}
                    icon={<BmGlobeIcon id="listings-icon" color={designColors.white} size="sm" />}
                  />
                </SidebarLink>
              </SidebarItem>
            </SidebarSection>
          </Can>

          {isAnyWarehouse && (
            <Can I="read" a="fulfillment">
              <SidebarSection>
                <SidebarSubList
                  id="fulfillment"
                  title={t("sidebar.group.fulfillment")}
                  icon={<BmWarehouseIcon id="fulfillment-icon" color={designColors.white} size="xs" />}
                  path={
                    [
                      isStockMovement && STOCK_MOVEMENTS_ROOT_PATH,
                      WAREHOUSES_ROOT_PATH,
                      FULFILLMENT_RULES_ROOT_PATH,
                    ].filter(Boolean) as string[]
                  }
                  tooltipTitle={t("sidebar.tooltip")}
                  disabled={isNotMaker}
                >
                  {isStockMovement && (
                    <SidebarSubItem
                      title={t("sidebar.stock_movements")}
                      to={STOCK_MOVEMENTS_ROOT_PATH}
                      path={STOCK_MOVEMENTS_ROOT_PATH}
                      disabled={isNotMaker}
                    />
                  )}
                  <SidebarSubItem
                    title={t("sidebar.warehouses")}
                    to={WAREHOUSES_ROOT_PATH}
                    path={WAREHOUSES_ROOT_PATH}
                    disabled={isNotMaker}
                  />
                  {allWarehousesData!.warehouses.length > 1 && (
                    <SidebarSubItem
                      title={t("sidebar.fulfillment_rules")}
                      to={FULFILLMENT_RULES_ROOT_PATH}
                      path={FULFILLMENT_RULES_ROOT_PATH}
                      disabled={isNotMaker}
                    />
                  )}
                </SidebarSubList>
              </SidebarSection>
            </Can>
          )}

          <Can I="read" a="finance">
            <SidebarItem
              disabled={isNotMaker}
              selected={[BALANCE_PATH, ORDER_SETTLEMENTS_PATH, FINANCIAL_STATEMENTS_PATH].some((pattern) =>
                matchPath({path: pattern, end: false}, location.pathname),
              )}
            >
              <SidebarLink to={BALANCE_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                <SidebarItemContent
                  text={t("sidebar.finances")}
                  icon={<BmDollarIcon id="finances-icon" color={designColors.white} size="sm" />}
                />
              </SidebarLink>
            </SidebarItem>
          </Can>

          <Can I="read" a="settings">
            <SidebarSection>
              <SidebarSubList
                id="settings"
                title={t("sidebar.group.settings")}
                icon={<BmSettingsIcon id="settings-icon" color={designColors.white} size="xs" />}
                path={[
                  COMPANY_INFO_ROOT_PATH,
                  USERS_PATH,
                  PAYMENT_INFO_ROOT_PATH,
                  LOCATIONS_ROOT_PATH,
                  NOTIFICATIONS_PATH,
                  BRANDS_PATH,
                ]}
                tooltipTitle={t("sidebar.tooltip")}
                disabled={isNotMaker}
              >
                <SidebarSubItem
                  title={t("sidebar.company_info")}
                  to={COMPANY_INFO_ROOT_PATH}
                  path={COMPANY_INFO_ROOT_PATH}
                  disabled={isNotMaker}
                />
                <SidebarSubItem title={t("sidebar.users")} to={USERS_PATH} path={USERS_PATH} disabled={isNotMaker} />
                <SidebarSubItem
                  title={t("sidebar.payment_info")}
                  to={PAYMENT_INFO_ROOT_PATH}
                  path={PAYMENT_INFO_ROOT_PATH}
                  disabled={isNotMaker}
                />
                <SidebarSubItem
                  title={t("sidebar.notifications")}
                  to={NOTIFICATIONS_PATH}
                  path={NOTIFICATIONS_PATH}
                  disabled={isNotMaker}
                />
                {isClickAndCollect && (
                  <SidebarSubItem
                    title={t("sidebar.click_and_collect")}
                    to={LOCATIONS_ROOT_PATH}
                    path={LOCATIONS_ROOT_PATH}
                    disabled={isNotMaker}
                  />
                )}
                {maker.trade && (
                  <SidebarSubItem
                    title={t("sidebar.brands")}
                    to={BRANDS_PATH}
                    path={BRANDS_PATH}
                    disabled={isNotMaker}
                  />
                )}
              </SidebarSubList>
            </SidebarSection>
          </Can>

          <Can I="manage" a="businessTools">
            <SidebarSection>
              <SidebarItem
                disabled={isNotMaker}
                selected={!!matchPath({path: BUSINESS_TOOLS_PATH, end: false}, location.pathname)}
              >
                <SidebarLink to={BUSINESS_TOOLS_PATH} tooltipTitle={t("sidebar.tooltip")} disabled={isNotMaker}>
                  <SidebarItemContent
                    text={t("sidebar.business_tools")}
                    icon={<BmBookOpenIcon id="book-icon" color={designColors.white} size="sm" />}
                  />
                </SidebarLink>
              </SidebarItem>
            </SidebarSection>
          </Can>

          <SidebarSection>
            <SidebarItem>
              <Link href={WATS_NEW_URL} target="_blank" underline="none" sx={{width: "100%"}}>
                <SidebarItemContent
                  text={t("sidebar.wats_new")}
                  icon={<BmEnergyIcon id="energy-icon" color={designColors.white} size="xs" />}
                />
              </Link>
            </SidebarItem>
          </SidebarSection>
        </Stack>
      </MenuList>
      <MenuList sx={{py: 4}}>
        <SidebarSection>
          <SidebarUser />
        </SidebarSection>
      </MenuList>
    </Box>
  );
};
